import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, MenuItem, TextField,
  Button, CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Stack, Alert, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Tooltip, LinearProgress, Tab, Tabs, InputAdornment,
  FormControlLabel, Switch, Autocomplete
} from '@mui/material';
import {
  AccountBalance, Add, Edit, Delete, Payment, Receipt, Block,
  TrendingUp, Warning, CheckCircle, HourglassEmpty,
  Search, FileDownload, Bolt, Refresh, Close, PriceCheck,
  Upload, Person, Class, Subject as SubjectIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { settingsAPI } from '../services/api';
import { exportCSV } from '../utils/export';
import axios from 'axios';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api' });
api.interceptors.request.use(c => {
  const t = localStorage.getItem('sms_token');
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

// ── Fee type config ────────────────────────────────────────────────────────────
const FEE_TYPES = [
  { value: 'tuition',          label: 'Tuition' },
  { value: 'exam',             label: 'Examination' },
  { value: 'sports',           label: 'Sports' },
  { value: 'boarding',         label: 'Boarding' },
  { value: 'registration',     label: 'Registration' },
  { value: 'school_trip',      label: 'School Trip' },
  { value: 'invigilation',     label: 'Invigilation' },
  { value: 'uniform',          label: 'Uniform' },
  { value: 'books_stationery', label: 'Books & Stationery' },
  { value: 'library',          label: 'Library' },
  { value: 'lost_book',        label: 'Lost Book' },
  { value: 'subject_fee',      label: 'Subject Fee' },
  { value: 'other',            label: 'Other (specify)' },
];

const PAY_METHODS = [
  { value: 'cash',          label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank Transfer / EFT' },
  { value: 'mobile_money',  label: 'Mobile Money' },
  { value: 'cheque',        label: 'Cheque' },
  { value: 'other',         label: 'Other' },
];

const STATUS_CFG = {
  paid:    { color: 'success', label: 'Paid',    icon: <CheckCircle sx={{ fontSize: 14 }} /> },
  partial: { color: 'warning', label: 'Partial', icon: <HourglassEmpty sx={{ fontSize: 14 }} /> },
  unpaid:  { color: 'error',   label: 'Unpaid',  icon: <Warning sx={{ fontSize: 14 }} /> },
  overdue: { color: 'error',   label: 'Overdue', icon: <Warning sx={{ fontSize: 14 }} /> },
  waived:  { color: 'default', label: 'Waived',  icon: <Close sx={{ fontSize: 14 }} /> },
};

const fmt  = (n) => `E${parseFloat(n || 0).toLocaleString('en', { minimumFractionDigits: 2 })}`;
const fmtD = (d) => d ? new Date(d).toLocaleDateString('en-GB') : '—';
const ftLabel = (ft) => FEE_TYPES.find(f => f.value === ft)?.label || ft || 'Manual';

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

function StatCard({ label, value, sub, color = '#0f2b4a', icon }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 2, borderLeft: `4px solid ${color}` }}>
      <CardContent sx={{ pb: '12px !important' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>{label}</Typography>
            <Typography variant="h5" fontWeight={800} sx={{ color, mt: 0.5 }}>{value}</Typography>
            {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
          </Box>
          <Box sx={{ color, opacity: 0.3, fontSize: 36 }}>{icon}</Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

// ── Fee Structure Dialog ───────────────────────────────────────────────────────
function StructureDialog({ open, onClose, onSave, editing, classes, subjects, academicYears }) {
  const BLANK = { name: '', class_id: '', subject_id: '', academic_year_id: '', fee_type: 'tuition', amount: '', due_date: '', description: '', other_description: '', gender_filter: 'all', student_type_filter: 'all' };
  const [form, setForm] = useState(BLANK);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (open) {
      if (editing) {
        setForm({
          name: editing.name || '', class_id: editing.class_id || '',
          subject_id: editing.subject_id || '', academic_year_id: editing.academic_year_id || '',
          fee_type: editing.fee_type || 'tuition', amount: editing.amount || '',
          due_date: editing.due_date ? editing.due_date.split('T')[0] : '',
          description: editing.description || '', other_description: editing.other_description || '',
          gender_filter: editing.gender_filter || 'all', student_type_filter: editing.student_type_filter || 'all'
        });
      } else {
        const cur = academicYears.find(y => y.is_current);
        setForm({ ...BLANK, academic_year_id: cur?.id || '' });
      }
    }
  }, [open, editing]);

  const isSubjectFee = form.fee_type === 'subject_fee';
  const isOther = form.fee_type === 'other';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editing ? 'Edit Fee Structure' : 'Add Fee Structure'}</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2, fontSize: 12 }}>
          Creating a fee structure will <strong>automatically generate invoices</strong> for all matching enrolled students. Editing will <strong>update existing unpaid invoices</strong> and add any missing ones.
        </Alert>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Fee Name *" value={form.name} onChange={set('name')} placeholder="e.g. Form 1 Tuition — Term 1" />
          </Grid>
          <Grid item xs={6}>
            <TextField select fullWidth size="small" label="Fee Type *" value={form.fee_type} onChange={set('fee_type')}>
              {FEE_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth size="small" type="number" label="Amount (E) *" value={form.amount} onChange={set('amount')}
              InputProps={{ startAdornment: <InputAdornment position="start">E</InputAdornment> }} />
          </Grid>
          {isOther && (
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Specify fee description *" value={form.other_description} onChange={set('other_description')}
                placeholder="e.g. Computer Lab maintenance" />
            </Grid>
          )}
          {isSubjectFee ? (
            <Grid item xs={12}>
              <TextField select fullWidth size="small" label="Subject (fee applies per subject enrolled)" value={form.subject_id} onChange={set('subject_id')}>
                <MenuItem value="">All subjects</MenuItem>
                {subjects.map(s => <MenuItem key={s.id} value={s.id}>{s.name}{s.is_core ? ' (Core)' : ''}</MenuItem>)}
              </TextField>
            </Grid>
          ) : (
            <Grid item xs={12}>
              <TextField select fullWidth size="small" label="Applies To Class (leave blank for all classes)" value={form.class_id} onChange={set('class_id')}>
                <MenuItem value="">All Classes (school-wide)</MenuItem>
                {classes.map(c => <MenuItem key={c.id} value={c.id}>{c.name}{c.stream ? ` (${c.stream})` : ''}</MenuItem>)}
              </TextField>
            </Grid>
          )}
          <Grid item xs={6}>
            <TextField select fullWidth size="small" label="Academic Year *" value={form.academic_year_id} onChange={set('academic_year_id')}>
              {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}{y.is_current ? ' (Current)' : ''}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth size="small" type="date" label="Due Date" value={form.due_date} onChange={set('due_date')} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6}>
            <TextField select fullWidth size="small" label="Applies To Gender" value={form.gender_filter || 'all'} onChange={set('gender_filter')}>
              <MenuItem value="all">All students</MenuItem>
              <MenuItem value="male">Male students only</MenuItem>
              <MenuItem value="female">Female students only</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField select fullWidth size="small" label="Applies To Student Type" value={form.student_type_filter || 'all'} onChange={set('student_type_filter')}>
              <MenuItem value="all">All students</MenuItem>
              <MenuItem value="new">New students only (first enrolment)</MenuItem>
              <MenuItem value="returning">Returning students only</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Internal Notes (optional)" value={form.description} onChange={set('description')} multiline rows={2} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(form)} sx={{ bgcolor: '#0f2b4a' }}>
          {editing ? 'Update' : 'Create & Auto-Bill'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Fee Structures Tab ─────────────────────────────────────────────────────────
function StructuresTab({ academicYears, classes, subjects }) {
  const { enqueueSnackbar } = useSnackbar();
  const [structures, setStructures] = useState([]);
  const [loading, setLoading]       = useState(false);
  const [selectedYear, setYear]     = useState('');
  const [dialog, setDialog]         = useState(false);
  const [editing, setEditing]       = useState(null);
  const [generating, setGenerating] = useState(null);

  const load = useCallback(async () => {
    if (!selectedYear) return;
    setLoading(true);
    try {
      const r = await api.get('/fees/structures', { params: { academic_year_id: selectedYear } });
      if (r.data.success) setStructures(r.data.data);
    } catch { enqueueSnackbar('Failed to load', { variant: 'error' }); }
    finally { setLoading(false); }
  }, [selectedYear, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const cur = academicYears.find(y => y.is_current); if (cur) setYear(cur.id); }, [academicYears]);

  const save = async (form) => {
    if (!form.name || !form.amount) return enqueueSnackbar('Name and amount required', { variant: 'warning' });
    try {
      let msg;
      if (editing) {
        await api.put(`/fees/structures/${editing.id}`, { ...form, is_active: 1 });
        msg = 'Fee structure updated';
      } else {
        const r = await api.post('/fees/structures', form);
        msg = r.data.message;
      }
      enqueueSnackbar(msg, { variant: 'success' });
      setDialog(false); setEditing(null); load();
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error', { variant: 'error' }); }
  };

  const del = async (s) => {
    if (!window.confirm(`Delete "${s.name}"?`)) return;
    try { await api.delete(`/fees/structures/${s.id}`); enqueueSnackbar('Deleted', { variant: 'success' }); load(); }
    catch (err) { enqueueSnackbar(err.response?.data?.message || 'Cannot delete', { variant: 'error' }); }
  };

  const rebill = async (s) => {
    setGenerating(s.id);
    try {
      const r = await api.post('/fees/generate-invoices', { fee_structure_id: s.id, academic_year_id: selectedYear });
      enqueueSnackbar(r.data.message, { variant: 'success' });
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error', { variant: 'error' }); }
    finally { setGenerating(null); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField select size="small" label="Academic Year" value={selectedYear} onChange={e => setYear(e.target.value)} sx={{ minWidth: 200 }}>
          {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}{y.is_current ? ' (Current)' : ''}</MenuItem>)}
        </TextField>
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="contained" startIcon={<Add />} onClick={() => { setEditing(null); setDialog(true); }} sx={{ bgcolor: '#0f2b4a' }}>
          Add Fee Structure
        </Button>
      </Box>

      {loading ? <CircularProgress /> : (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#0f2b4a' }}>
                  {['Fee Name', 'Type', 'Applies To', 'Amount', 'Due Date', 'Actions'].map(h => (
                    <TableCell key={h} sx={{ color: 'white', fontWeight: 700, fontSize: 11 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {structures.length === 0 && (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No fee structures yet. Create one to automatically bill students.
                  </TableCell></TableRow>
                )}
                {structures.map((s, i) => (
                  <TableRow key={s.id} hover sx={{ bgcolor: i % 2 === 0 ? 'white' : '#fafafa', opacity: s.is_active ? 1 : 0.5 }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>
                      {s.name}
                      {s.other_description && <Typography fontSize={10} color="text.secondary">→ {s.other_description}</Typography>}
                      {!s.is_active && <Chip label="Inactive" size="small" sx={{ ml: 1, fontSize: 9 }} />}
                    </TableCell>
                    <TableCell><Chip label={ftLabel(s.fee_type)} size="small" sx={{ fontSize: 10 }} /></TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      {s.subject_name ? (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <SubjectIcon sx={{ fontSize: 14 }} />
                          <Typography fontSize={12}>{s.subject_name}</Typography>
                        </Stack>
                      ) : s.class_name ? (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Class sx={{ fontSize: 14 }} />
                          <Typography fontSize={12}>{s.class_name}{s.stream ? ` (${s.stream})` : ''}</Typography>
                        </Stack>
                      ) : 'All Classes'}
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={700} color="#0f2b4a">{fmt(s.amount)}</Typography>
                      <Stack direction="row" spacing={0.5} mt={0.3}>
                        {s.gender_filter && s.gender_filter !== 'all' && (
                          <Chip label={s.gender_filter === 'male' ? '♂ Male only' : '♀ Female only'} size="small" color={s.gender_filter === 'male' ? 'info' : 'secondary'} sx={{ fontSize: 9, height: 16 }} />
                        )}
                        {s.student_type_filter && s.student_type_filter !== 'all' && (
                          <Chip label={s.student_type_filter === 'new' ? '🆕 New only' : '🔁 Returning only'} size="small" color="default" sx={{ fontSize: 9, height: 16 }} />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{fmtD(s.due_date)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Re-bill missing students">
                          <IconButton size="small" color="primary" onClick={() => rebill(s)} disabled={generating === s.id}>
                            {generating === s.id ? <CircularProgress size={14} /> : <Bolt fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => { setEditing(s); setDialog(true); }}><Edit fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => del(s)}><Delete fontSize="small" /></IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      <StructureDialog
        open={dialog} onClose={() => { setDialog(false); setEditing(null); }}
        onSave={save} editing={editing}
        classes={classes} subjects={subjects} academicYears={academicYears}
      />
    </Box>
  );
}

// ── Invoices & Payments Tab ────────────────────────────────────────────────────
function InvoicesTab({ academicYears, classes, students, subjects }) {
  const { enqueueSnackbar } = useSnackbar();
  const [invoices, setInvoices]     = useState([]);
  const [loading, setLoading]       = useState(false);
  const [selectedYear, setYear]     = useState('');
  const [filterStatus, setStatus]   = useState('');
  const [filterClass, setClass]     = useState('');
  const [search, setSearch]         = useState('');
  const [payDialog, setPayDialog]   = useState(null);
  const [editDialog, setEditDialog] = useState(null);  // invoice being edited
  const [bulkDialog, setBulk]       = useState(false);
  const [paymentsView, setPayView]  = useState(null);
  const [payments, setPayments]     = useState([]);
  const [payForm, setPayForm]       = useState({ amount: '', payment_method: 'cash', reference: '', payment_date: new Date().toISOString().split('T')[0], notes: '' });
  const [editForm, setEditForm]     = useState({ amount: '', due_date: '', description: '', status: '', notes: '' });
  const [bulkForm, setBulkForm]     = useState({ payment_method: 'cash', payment_date: new Date().toISOString().split('T')[0] });
  const [bulkAmounts, setBulkAmts]  = useState({});

  const load = useCallback(async () => {
    if (!selectedYear) return;
    setLoading(true);
    try {
      const r = await api.get('/fees/invoices', { params: { academic_year_id: selectedYear, status: filterStatus || undefined, class_id: filterClass || undefined } });
      if (r.data.success) setInvoices(r.data.data);
    } catch { enqueueSnackbar('Failed to load invoices', { variant: 'error' }); }
    finally { setLoading(false); }
  }, [selectedYear, filterStatus, filterClass, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const cur = academicYears.find(y => y.is_current); if (cur) setYear(cur.id); }, [academicYears]);

  const openPay = (inv) => {
    setPayDialog(inv);
    setPayForm({ amount: Math.max(0, parseFloat(inv.amount) - parseFloat(inv.amount_paid)).toFixed(2), payment_method: 'cash', reference: '', payment_date: new Date().toISOString().split('T')[0], notes: '' });
  };

  const recordPayment = async () => {
    try {
      const r = await api.post('/fees/payments', { invoice_id: payDialog.id, ...payForm });
      enqueueSnackbar(`Payment recorded — Receipt: ${r.data.receipt_number}`, { variant: 'success' });
      setPayDialog(null); load();
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error', { variant: 'error' }); }
  };

  const viewPayments = async (inv) => {
    const r = await api.get('/fees/payments', { params: { invoice_id: inv.id } });
    setPayments(r.data.data); setPayView(inv);
  };

  const reversePayment = async (payId) => {
    if (!window.confirm('Reverse this payment?')) return;
    try { await api.delete(`/fees/payments/${payId}`); enqueueSnackbar('Reversed', { variant: 'success' }); viewPayments(paymentsView); load(); }
    catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error', { variant: 'error' }); }
  };

  const openEdit = (inv) => {
    setEditDialog(inv);
    setEditForm({
      amount: inv.amount, due_date: inv.due_date ? inv.due_date.split('T')[0] : '',
      description: inv.description, status: inv.status, notes: inv.notes || ''
    });
  };

  const saveEdit = async () => {
    try {
      await api.put(`/fees/invoices/${editDialog.id}`, editForm);
      enqueueSnackbar('Invoice updated', { variant: 'success' });
      setEditDialog(null); load();
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error', { variant: 'error' }); }
  };

  const waiveInvoice = async (id) => {
    const reason = window.prompt('Reason for waiving this invoice (optional):');
    if (reason === null) return; // cancelled
    try {
      await api.post(`/fees/invoices/${id}/waive`, { notes: reason });
      enqueueSnackbar('Invoice waived', { variant: 'success' }); load();
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error', { variant: 'error' }); }
  };

  const submitBulkPayment = async () => {
    const payments = Object.entries(bulkAmounts)
      .filter(([, amt]) => amt && parseFloat(amt) > 0)
      .map(([invoice_id, amount]) => ({ invoice_id: parseInt(invoice_id), amount: parseFloat(amount) }));
    if (!payments.length) return enqueueSnackbar('Enter at least one payment amount', { variant: 'warning' });
    try {
      const r = await api.post('/fees/payments/bulk', { payments, ...bulkForm });
      enqueueSnackbar(r.data.message, { variant: 'success' });
      setBulk(false); setBulkAmts({}); load();
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error', { variant: 'error' }); }
  };

  const deleteInvoice = async (id) => {
    if (!window.confirm('Delete this invoice?')) return;
    try { await api.delete(`/fees/invoices/${id}`); enqueueSnackbar('Deleted', { variant: 'success' }); load(); }
    catch (err) { enqueueSnackbar(err.response?.data?.message || 'Cannot delete', { variant: 'error' }); }
  };

  const filtered = invoices.filter(inv => !search ||
    `${inv.first_name} ${inv.last_name} ${inv.admission_number} ${inv.invoice_number}`.toLowerCase().includes(search.toLowerCase())
  );
  const totalBilled    = filtered.reduce((s, i) => s + parseFloat(i.amount), 0);
  const totalCollected = filtered.reduce((s, i) => s + parseFloat(i.amount_paid), 0);
  const unpaidFiltered = filtered.filter(i => i.status !== 'paid' && i.status !== 'waived');

  return (
    <Box>
      {/* Filters */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={3}>
          <TextField select size="small" fullWidth label="Academic Year" value={selectedYear} onChange={e => setYear(e.target.value)}>
            {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}{y.is_current ? ' (Current)' : ''}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={2}>
          <TextField select size="small" fullWidth label="Status" value={filterStatus} onChange={e => setStatus(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {Object.entries(STATUS_CFG).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={2}>
          <TextField select size="small" fullWidth label="Class" value={filterClass} onChange={e => setClass(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {classes.map(c => <MenuItem key={c.id} value={c.id}>{c.name}{c.stream ? ` (${c.stream})` : ''}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={5}>
          <TextField size="small" fullWidth placeholder="Search student or invoice…" value={search} onChange={e => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
        </Grid>
      </Grid>

      {/* Action bar */}
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
        <Paper variant="outlined" sx={{ px: 2, py: 1, borderRadius: 2 }}>
          <Typography variant="caption" color="text.secondary">Total Billed</Typography>
          <Typography fontWeight={700} color="#0f2b4a">{fmt(totalBilled)}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ px: 2, py: 1, borderRadius: 2 }}>
          <Typography variant="caption" color="text.secondary">Collected</Typography>
          <Typography fontWeight={700} color="success.main">{fmt(totalCollected)}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ px: 2, py: 1, borderRadius: 2 }}>
          <Typography variant="caption" color="text.secondary">Outstanding</Typography>
          <Typography fontWeight={700} color="error.main">{fmt(totalBilled - totalCollected)}</Typography>
        </Paper>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Refresh"><IconButton onClick={load} size="small"><Refresh /></IconButton></Tooltip>
        <Button variant="outlined" size="small" startIcon={<Upload />} onClick={() => setBulk(true)}>
          Upload Payments
        </Button>

        <Button size="small" variant="outlined" startIcon={<FileDownload />} onClick={() => {
          const h = ['Invoice #', 'Student', 'Admission', 'Class', 'Description', 'Type', 'Amount', 'Paid', 'Balance', 'Status', 'Due Date'];
          const r = filtered.map(i => [i.invoice_number, `${i.first_name} ${i.last_name}`, i.admission_number, i.class_name || '', i.description || '', ftLabel(i.fee_type), i.amount, i.amount_paid, (i.amount - i.amount_paid).toFixed(2), i.status, fmtD(i.due_date)]);
          exportCSV('fee_invoices', h, r);
        }}>Export CSV</Button>
      </Stack>

      {loading ? <CircularProgress /> : (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <TableContainer sx={{ maxHeight: 520 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['Invoice #', 'Student', 'Class', 'Description / Type', 'Amount', 'Paid', 'Balance', 'Status', 'Due', 'Actions'].map(h => (
                    <TableCell key={h} sx={{ bgcolor: '#0f2b4a', color: 'white', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 && (
                  <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>No invoices found</TableCell></TableRow>
                )}
                {filtered.map((inv, i) => {
                  const bal = parseFloat(inv.amount) - parseFloat(inv.amount_paid);
                  const cfg = STATUS_CFG[inv.status] || STATUS_CFG.unpaid;
                  return (
                    <TableRow key={inv.id} hover sx={{ bgcolor: i % 2 === 0 ? 'white' : '#fafafa' }}>
                      <TableCell sx={{ fontSize: 10, fontFamily: 'monospace', color: 'primary.main', fontWeight: 600 }}>{inv.invoice_number}</TableCell>
                      <TableCell>
                        <Typography fontSize={12} fontWeight={600}>{inv.last_name}, {inv.first_name}</Typography>
                        <Typography fontSize={10} color="text.secondary">{inv.admission_number}</Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: 11 }}>{inv.class_name || '—'}</TableCell>
                      <TableCell>
                        <Typography fontSize={12}>{inv.description}</Typography>
                        <Chip label={ftLabel(inv.fee_type)} size="small" sx={{ fontSize: 9, height: 16 }} />
                        {inv.subject_name && <Chip label={inv.subject_name} size="small" color="secondary" sx={{ fontSize: 9, height: 16, ml: 0.5 }} />}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>{fmt(inv.amount)}</TableCell>
                      <TableCell sx={{ fontSize: 12, color: 'success.main', fontWeight: 600 }}>{fmt(inv.amount_paid)}</TableCell>
                      <TableCell sx={{ fontSize: 12, fontWeight: 700, color: bal > 0 ? 'error.main' : 'success.main' }}>{fmt(bal)}</TableCell>
                      <TableCell><Chip icon={cfg.icon} label={cfg.label} size="small" color={cfg.color} sx={{ fontSize: 10, fontWeight: 700 }} /></TableCell>
                      <TableCell sx={{ fontSize: 11, color: 'text.secondary', whiteSpace: 'nowrap' }}>{fmtD(inv.due_date)}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.3}>
                          {inv.status !== 'paid' && inv.status !== 'waived' && (
                            <Tooltip title="Record Payment">
                              <IconButton size="small" color="success" onClick={() => openPay(inv)}><Payment fontSize="small" /></IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Payment history">
                            <IconButton size="small" color="primary" onClick={() => viewPayments(inv)}><Receipt fontSize="small" /></IconButton>
                          </Tooltip>
                          {inv.status !== 'paid' && (
                            <Tooltip title="Edit / Adjust invoice">
                              <IconButton size="small" color="warning" onClick={() => openEdit(inv)}><Edit fontSize="small" /></IconButton>
                            </Tooltip>
                          )}
                          {inv.status !== 'waived' && inv.status !== 'paid' && (
                            <Tooltip title="Waive invoice">
                              <IconButton size="small" color="default" onClick={() => waiveInvoice(inv.id)}><Block fontSize="small" /></IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Record Payment Dialog */}
      <Dialog open={!!payDialog} onClose={() => setPayDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Record Payment — {payDialog?.invoice_number}</DialogTitle>
        <DialogContent dividers>
          {payDialog && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f5f6fa', borderRadius: 1 }}>
              <Typography fontSize={13} fontWeight={600}>{payDialog.first_name} {payDialog.last_name}</Typography>
              <Typography fontSize={12}>{payDialog.description}</Typography>
              <Typography fontSize={12}>
                Total: {fmt(payDialog.amount)} | Paid: {fmt(payDialog.amount_paid)} |
                <strong style={{ color: '#c62828' }}> Balance: {fmt(parseFloat(payDialog.amount) - parseFloat(payDialog.amount_paid))}</strong>
              </Typography>
            </Box>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" type="number" label="Amount Received (E) *" value={payForm.amount} onChange={e => setPayForm(f => ({ ...f, amount: e.target.value }))}
                InputProps={{ startAdornment: <InputAdornment position="start">E</InputAdornment> }} />
            </Grid>
            <Grid item xs={12}>
              <TextField select fullWidth size="small" label="Payment Method" value={payForm.payment_method} onChange={e => setPayForm(f => ({ ...f, payment_method: e.target.value }))}>
                {PAY_METHODS.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Reference / Bank Ref / Cheque No." value={payForm.reference} onChange={e => setPayForm(f => ({ ...f, reference: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" type="date" label="Payment Date *" value={payForm.payment_date} onChange={e => setPayForm(f => ({ ...f, payment_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Notes" value={payForm.notes} onChange={e => setPayForm(f => ({ ...f, notes: e.target.value }))} multiline rows={2} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayDialog(null)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={recordPayment} startIcon={<Payment />}>Record Payment</Button>
        </DialogActions>
      </Dialog>

      {/* Payment History Dialog */}
      <Dialog open={!!paymentsView} onClose={() => setPayView(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Payment History — {paymentsView?.invoice_number}</DialogTitle>
        <DialogContent dividers>
          {payments.length === 0 ? (
            <Typography color="text.secondary" align="center" sx={{ py: 3 }}>No payments recorded yet</Typography>
          ) : (
            <Table size="small">
              <TableHead><TableRow>
                {['Receipt', 'Date', 'Amount', 'Method', 'Reference', 'Received By', ''].map(h => <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11 }}>{h}</TableCell>)}
              </TableRow></TableHead>
              <TableBody>
                {payments.map(p => (
                  <TableRow key={p.id}>
                    <TableCell sx={{ fontSize: 11, fontFamily: 'monospace' }}>{p.receipt_number}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{fmtD(p.payment_date)}</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'success.main' }}>{fmt(p.amount)}</TableCell>
                    <TableCell><Chip label={PAY_METHODS.find(m => m.value === p.payment_method)?.label || p.payment_method} size="small" sx={{ fontSize: 10 }} /></TableCell>
                    <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>{p.reference || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>{p.received_by_name || '—'}</TableCell>
                    <TableCell><Tooltip title="Reverse"><IconButton size="small" color="error" onClick={() => reversePayment(p.id)}><Delete fontSize="small" /></IconButton></Tooltip></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setPayView(null)}>Close</Button></DialogActions>
      </Dialog>

      {/* Edit / Adjust Invoice Dialog */}
      <Dialog open={!!editDialog} onClose={() => setEditDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Adjust Invoice — {editDialog?.invoice_number}</DialogTitle>
        <DialogContent dividers>
          {editDialog && (
            <Alert severity="warning" sx={{ mb: 2, fontSize: 12 }}>
              This adjusts an auto-billed invoice for a student query or special case.
              <strong> {editDialog.first_name} {editDialog.last_name}</strong>
            </Alert>
          )}
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={8}>
              <TextField fullWidth size="small" label="Description" value={editForm.description}
                onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth size="small" type="number" label="Amount (E)" value={editForm.amount}
                onChange={e => setEditForm(f => ({ ...f, amount: e.target.value }))}
                InputProps={{ startAdornment: <InputAdornment position="start">E</InputAdornment> }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="date" label="Due Date" value={editForm.due_date}
                onChange={e => setEditForm(f => ({ ...f, due_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth size="small" label="Status" value={editForm.status}
                onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}>
                {Object.entries(STATUS_CFG).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Reason / Notes *" value={editForm.notes}
                onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                multiline rows={2} helperText="Document why this invoice was adjusted" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(null)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={saveEdit} startIcon={<Edit />}>Save Adjustment</Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Payment Upload Dialog */}
      <Dialog open={bulkDialog} onClose={() => setBulk(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Upload /> Upload Payments</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2, fontSize: 12 }}>
            Enter the amount received for each student below. Leave blank to skip. All will use the same payment method and date.
          </Alert>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <TextField select fullWidth size="small" label="Payment Method *" value={bulkForm.payment_method} onChange={e => setBulkForm(f => ({ ...f, payment_method: e.target.value }))}>
                {PAY_METHODS.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="date" label="Payment Date *" value={bulkForm.payment_date} onChange={e => setBulkForm(f => ({ ...f, payment_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
          </Grid>
          <Card variant="outlined" sx={{ borderRadius: 1 }}>
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#0f2b4a' }}>
                    {['Student', 'Invoice #', 'Description', 'Balance Due', 'Amount Paying'].map(h => (
                      <TableCell key={h} sx={{ bgcolor: '#0f2b4a', color: 'white', fontWeight: 700, fontSize: 11 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {unpaidFiltered.length === 0 && (
                    <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>No unpaid invoices in current filter</TableCell></TableRow>
                  )}
                  {unpaidFiltered.map(inv => {
                    const bal = parseFloat(inv.amount) - parseFloat(inv.amount_paid);
                    return (
                      <TableRow key={inv.id} hover>
                        <TableCell>
                          <Typography fontSize={12} fontWeight={600}>{inv.last_name}, {inv.first_name}</Typography>
                          <Typography fontSize={10} color="text.secondary">{inv.admission_number}</Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: 10, fontFamily: 'monospace' }}>{inv.invoice_number}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{inv.description}</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'error.main', fontSize: 12 }}>{fmt(bal)}</TableCell>
                        <TableCell>
                          <TextField size="small" type="number" placeholder={`Max ${fmt(bal)}`}
                            value={bulkAmounts[inv.id] || ''}
                            onChange={e => setBulkAmts(prev => ({ ...prev, [inv.id]: e.target.value }))}
                            sx={{ width: 120, '& .MuiInputBase-input': { fontSize: 12, py: '4px' } }}
                            InputProps={{ startAdornment: <InputAdornment position="start" sx={{ fontSize: 11 }}>E</InputAdornment> }} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
          <Box sx={{ mt: 1.5, textAlign: 'right' }}>
            <Typography fontSize={12} color="text.secondary">
              Total entering: <strong style={{ color: '#2e7d32' }}>
                {fmt(Object.values(bulkAmounts).reduce((s, v) => s + (parseFloat(v) || 0), 0))}
              </strong>
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setBulk(false); setBulkAmts({}); }}>Cancel</Button>
          <Button variant="contained" color="success" onClick={submitBulkPayment} startIcon={<Upload />}>
            Upload Payments
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Overview Tab ───────────────────────────────────────────────────────────────
function OverviewTab({ academicYears }) {
  const { enqueueSnackbar } = useSnackbar();
  const [stats, setStats]       = useState(null);
  const [loading, setLoading]   = useState(false);
  const [selectedYear, setYear] = useState('');

  useEffect(() => { const cur = academicYears.find(y => y.is_current); if (cur) setYear(cur.id); }, [academicYears]);
  useEffect(() => {
    if (!selectedYear) return;
    setLoading(true);
    api.get('/fees/stats', { params: { academic_year_id: selectedYear } })
      .then(r => { if (r.data.success) setStats(r.data.data); })
      .catch(() => enqueueSnackbar('Failed to load stats', { variant: 'error' }))
      .finally(() => setLoading(false));
  }, [selectedYear, enqueueSnackbar]);

  const s = stats?.summary;
  const pct = s?.total_billed > 0 ? ((s.total_collected / s.total_billed) * 100).toFixed(1) : 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <TextField select size="small" label="Academic Year" value={selectedYear} onChange={e => setYear(e.target.value)} sx={{ minWidth: 200 }}>
          {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}{y.is_current ? ' (Current)' : ''}</MenuItem>)}
        </TextField>
      </Box>
      {loading ? <CircularProgress /> : s && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}><StatCard label="Total Billed" value={fmt(s.total_billed)} color="#0f2b4a" icon={<AccountBalance />} /></Grid>
            <Grid item xs={6} sm={3}><StatCard label="Collected" value={fmt(s.total_collected)} color="#2e7d32" sub={`${pct}% rate`} icon={<TrendingUp />} /></Grid>
            <Grid item xs={6} sm={3}><StatCard label="Outstanding" value={fmt(s.total_outstanding)} color="#c62828" sub={`${(s.unpaid_count||0)+(s.partial_count||0)} invoices`} icon={<Warning />} /></Grid>
            <Grid item xs={6} sm={3}><StatCard label="Fully Paid" value={s.paid_count||0} color="#1565c0" sub={`of ${s.total_invoices} invoices`} icon={<CheckCircle />} /></Grid>
          </Grid>

          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" fontWeight={600}>Fee Collection Rate</Typography>
              <Typography variant="caption" fontWeight={700} color={pct >= 80 ? 'success.main' : 'error.main'}>{pct}%</Typography>
            </Box>
            <LinearProgress variant="determinate" value={Math.min(pct, 100)}
              color={pct >= 80 ? 'success' : pct >= 50 ? 'warning' : 'error'}
              sx={{ height: 10, borderRadius: 5 }} />
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography fontWeight={700} gutterBottom>By Fee Type</Typography>
                  {(stats.by_type || []).map(bt => (
                    <Box key={bt.fee_type} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography fontSize={13}>{ftLabel(bt.fee_type)}</Typography>
                      <Stack direction="row" spacing={1}>
                        <Typography fontSize={12} color="success.main">{fmt(bt.collected)}</Typography>
                        <Typography fontSize={12} color="text.secondary">/ {fmt(bt.billed)}</Typography>
                      </Stack>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography fontWeight={700} gutterBottom>Recent Payments</Typography>
                  {!stats.recent_payments?.length && <Typography fontSize={12} color="text.secondary">No payments yet</Typography>}
                  {stats.recent_payments?.map((p, i) => (
                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: i < stats.recent_payments.length - 1 ? '1px solid #eee' : 'none' }}>
                      <Box>
                        <Typography fontSize={12} fontWeight={600}>{p.first_name} {p.last_name}</Typography>
                        <Typography fontSize={10} color="text.secondary">{p.receipt_number} · {fmtD(p.payment_date)}</Typography>
                      </Box>
                      <Typography fontSize={13} fontWeight={700} color="success.main">{fmt(p.amount)}</Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function FeeManagement() {
  const [tab, setTab]           = useState(0);
  const [academicYears, setAY]  = useState([]);
  const [classes, setClasses]   = useState([]);
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);

  useEffect(() => {
    settingsAPI.getAcademicYears().then(r => { if (r.data.success) setAY(r.data.data); }).catch(() => {});
    settingsAPI.getClasses().then(r => { if (r.data.success) setClasses(r.data.data); }).catch(() => {});
    api.get('/students', { params: { limit: 2000, status: 'active' } }).then(r => { if (r.data.success) setStudents(r.data.data); }).catch(() => {});
    api.get('/subjects').then(r => { if (r.data.success) setSubjects(r.data.data); }).catch(() => {});
  }, []);

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountBalance color="primary" /> Fee Management
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Fee structures · Invoices · Payments · Auto-billing
        </Typography>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Tab icon={<TrendingUp fontSize="small" />} label="Overview" iconPosition="start" sx={{ fontSize: 12 }} />
        <Tab icon={<PriceCheck fontSize="small" />} label="Fee Structures" iconPosition="start" sx={{ fontSize: 12 }} />
        <Tab icon={<Receipt fontSize="small" />} label="Invoices & Payments" iconPosition="start" sx={{ fontSize: 12 }} />
      </Tabs>

      <TabPanel value={tab} index={0}><OverviewTab academicYears={academicYears} /></TabPanel>
      <TabPanel value={tab} index={1}><StructuresTab academicYears={academicYears} classes={classes} subjects={subjects} /></TabPanel>
      <TabPanel value={tab} index={2}><InvoicesTab academicYears={academicYears} classes={classes} students={students} subjects={subjects} /></TabPanel>
    </Box>
  );
}
